/**
 * Action types and interfaces for the action feature
 */
import { ActionUIState } from './store/slice';
import { PatientInfo } from './store/types';
import { ActionState, ActionType } from './constants';
import type { ModalType } from '../../components/modals/constants';

// Extend the root state via declaration merging
declare module '@store/types' {
  interface DynamicFeatureState {
    'actions/ui'?: ActionUIState;
  }
}

// Re-export generated types
export type * from './store/types';

// Re-export constants for backward compatibility
export { ActionState, ActionType };

/**
 * Base interface for all action types
 */
export interface BaseAction<T extends keyof ActionPayloadMap = keyof ActionPayloadMap> {
  action_id: string;
  provider_id: string;
  encounter_id: string;
  state: ActionState;
  action_type: T;
  action_payload: ActionPayloadMap[T];
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
  patient?: PatientInfo;
}

/**
 * Payload for schedule appointment actions
 */
export interface ScheduleAppointmentPayload {
  scheduled_for: string; // ISO date string
  scheduled_duration: number; // Duration in minutes
  encounter_type_id?: string; // UUID of the encounter type
  encounter_type?: string; // Name of the encounter type
  reason?: string; // Reason for appointment
}

/**
 * ICD-10 code with details
 */
export interface ICD10Code {
  code: string;
  name: string;
}

/**
 * Modifier code with details
 */
export interface Modifier {
  code: string;
  name: string;
}

/**
 * CPT code with associated ICD-10 codes and modifiers
 */
export interface CPTCode {
  code: string;
  name: string;
  icd10_codes: ICD10Code[];
  modifiers: Modifier[];
}

/**
 * Payload for generate claim actions
 */
export interface GenerateClaimPayload {
  cpt_codes: CPTCode[];
  justification: string;
}

/**
 * Payload for PEP-C (Post-Encounter Patient Communication) actions
 */
export interface PepCPayload {
  title: string;
  body: string; // Markdown content
  reasoning_trace: string;
}

/**
 * Payload for payment request actions
 */
export interface PaymentRequestPayload {
  patient_name: string;
  appointment_date: string | null; // YYYY-MM-DD format
  reason_for_payment: string | null;
  amount: string | null;
  due_date: string | null; // YYYY-MM-DD format
  encounter_id: string;
  appointment_id: string | null;
}

export interface TemplateSection {
  module_id: string;
  display_name: string;
  should_remove: boolean;
}

/**
 * Payload for template cleanup actions
 */
export interface TemplateCleanupPayload {
  template_id: string;
  template_name: string;
  sections: TemplateSection[];
}

/**
 * Lab order data structure
 */
export interface LabOrderData {
  patient_id: string;
  lab_tests: string[];
  icd_10_codes: string[];
  special_instructions: string;
  sign_with_name: boolean;
  bundle_names: string[];
  created_at: string;
}

/**
 * Payload for lab order suggestion actions
 */
export interface LabOrderSuggestionPayload {
  justification: string;
  lab_order: LabOrderData;
}

/**
 * Patient field types that can be updated
 */
export enum PatientField {
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  EMAIL = 'email',
  PHONE = 'phone',
  ADDRESS = 'address',
  DATE_OF_BIRTH = 'date_of_birth',
  GENDER = 'gender',
  EMERGENCY_CONTACT_NAME = 'emergency_contact_name',
  EMERGENCY_CONTACT_PHONE = 'emergency_contact_phone',
  INSURANCE_MEMBER_ID = 'insurance_member_id',
  INSURANCE_GROUP_ID = 'insurance_group_id',
}

/**
 * A single patient field update operation
 */
export interface PatientFieldOperation {
  field: PatientField;
  field_display_name?: string; // Human-readable field name, fallback to field if not provided
  value: string;
  previous_value: string | null;
  source_form_field_id: string;
  confidence_score: number; // 0.0 to 1.0
}

/**
 * Payload for patient update actions
 */
export interface PatientUpdatePayload {
  patient_id: string;
  form_session_id: string;
  form_title: string;
  submitted_at: string; // ISO datetime
  operations: PatientFieldOperation[];
}

/**
 * Map of action types to their respective payloads
 * Add new action types here
 */
export interface ActionPayloadMap {
  schedule_appointment: ScheduleAppointmentPayload;
  billing_codes: GenerateClaimPayload;
  pep_c: PepCPayload;
  payment_request: PaymentRequestPayload;
  template_cleanup: TemplateCleanupPayload;
  lab_order_suggestion: LabOrderSuggestionPayload;
  patient_update: PatientUpdatePayload;
}

/**
 * Type for a complete action with known type
 */
export type Action = BaseAction<keyof ActionPayloadMap>;

/**
 * Scope for action lists
 */
export type ActionScope = 'dashboard' | 'note';

/**
 * Rejection reason enum for tracking why users reject suggestions
 */
export enum RejectionReason {
  WRONG_SUGGESTION = 'wrong_suggestion',
  DONT_CARE_FOR_FEATURE = 'dont_care_for_feature',
  NOT_FOR_THIS_TEMPLATE = 'not_for_this_template',
  OTHER = 'other',
}

/**
 * Context provided to rejection modal content components
 */
export interface RejectionModalContext {
  action: Action;
  actionType: ActionType;
  patientName?: string;
}

/**
 * Metadata passed with rejection for centralized tracking
 */
export interface RejectionMetadata {
  feedback?: string;
  editedTitle?: string;
  editedBody?: string;
  templateId?: string;
  // Could add other metadata as needed for future action types
}

/**
 * Interface for rejection modal content components
 */
export interface RejectionModalContentProps {
  context: RejectionModalContext;
  onReject: (reason: RejectionReason, metadata?: RejectionMetadata) => void;
  onCancel: () => void;
  onAccept?: (actionId: string, updatedPayload: any) => void;
  onCommand?: (command: ActionCommand) => void;
  isLoading?: boolean;
}

/**
 * Type mapping for card types to their supported rejection reasons
 */
export interface CardTypeRejectionReasons {
  schedule_appointment: RejectionReason[];
  billing_codes: RejectionReason[];
}

/**
 * Props interface for card content components
 */
export interface CardContentProps {
  action: BaseAction;

  // Command pattern
  onCommand: (command: ActionCommand) => void;
}

/**
 * Props interface for accept modal content components (future use)
 */
export interface AcceptModalContentProps {
  context: RejectionModalContext; // Reusing context structure for consistency
  onAccept: (actionId: string, updatedPayload?: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Context provided to edit modal content components
 */
export interface EditModalContext {
  action: Action;
  actionType: ActionType;
  patientName?: string;
}

/**
 * Props interface for edit modal content components
 */
export interface EditModalContentProps {
  context: EditModalContext;
  onSave: (updatedPayload: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Base interface for all action commands
 */
export interface ActionCommand<T = any> {
  type: ActionCommandType; // Allow string for extensibility
  payload?: T;
  metadata?: Record<string, any>;
}

/**
 * Standard command types used across action types
 */
export enum ActionCommandType {
  // Edit commands
  EDIT = 'EDIT',
  SAVE_EDIT = 'SAVE_EDIT',
  CANCEL_EDIT = 'CANCEL_EDIT',
  EDIT_IN_MODAL = 'EDIT_IN_MODAL',

  // Action commands
  ACCEPT = 'ACCEPT',
  ACCEPT_WITH_EDITS = 'ACCEPT_WITH_EDITS',
  REJECT = 'REJECT',

  // Display commands
  EXPAND = 'EXPAND',
  COLLAPSE = 'COLLAPSE',
}

/**
 * Validation function signature for commands
 */
export type CommandValidation<T = any> = (
  action: BaseAction,
  command: ActionCommand<T>,
) => { isValid: boolean; error?: string };

/**
 * Configuration for a specific command type
 */
export interface CommandConfig<T = any> {
  validation?: CommandValidation<T>;
  requiresConfirmation?: boolean;
  confirmationModal?: React.ComponentType<any>;
  allowedStates?: ActionState[];
}

/**
 * Context provided to onboarding check functions
 */
export interface OnboardingCheckContext {
  onboardingSteps: Record<string, boolean>;
  userState: any; // Full user state object
  action: BaseAction;
}

/**
 * Configuration for onboarding requirements
 */
export interface OnboardingConfig {
  /**
   * Key in userState.onboardingSteps to check (legacy)
   */
  onboardingKey?: string;

  /**
   * Dynamic check function for custom onboarding logic
   */
  checkFunction?: (context: OnboardingCheckContext) => boolean;

  /**
   * Modal to show for onboarding
   */
  modalType: ModalType;
}

/**
 * Result of validation check
 */
export interface ValidationResult {
  isValid: boolean;
  missingFields?: string[];
  errorMessage?: string;
}

/**
 * Configuration for action validation
 */
export interface ValidationConfig {
  /**
   * Function that validates the action data
   */
  validate: (action: BaseAction) => ValidationResult;

  /**
   * Modal to show when validation fails
   */
  validationModal?: ModalType;
}

/**
 * Configuration options for action type behavior
 */
export interface ActionTypeConfig {
  /** If true, allows inline editing of action details */
  allowsInlineEditing?: boolean;

  /** If true, allows modal-based editing of action details */
  allowsModalEditing?: boolean;

  /** Optional onboarding requirements for this action type */
  requiresOnboarding?: OnboardingConfig[];

  /** Optional validation configuration for this action type */
  validation?: ValidationConfig;

  /** Command configurations for this action type */
  commands?: {
    [commandType: string]: CommandConfig;
  };
}

/**
 * Complete definition for an action type including all components and configuration
 */
export interface ActionTypeDefinition {
  /** Required: Component for rendering the action card content */
  cardContent: React.ComponentType<CardContentProps>;

  /** Optional: Component for rejection modal content */
  rejectionModal?: React.ComponentType<RejectionModalContentProps>;

  /** Optional: Component for accept modal content */
  acceptModal?: React.ComponentType<AcceptModalContentProps>;

  /** Optional: Component for edit modal content */
  editModal?: React.ComponentType<EditModalContentProps>;

  /** Optional: Behavior configuration for this action type */
  config?: ActionTypeConfig;
}

/**
 * Registry mapping action types to their complete definitions
 */
export type ActionTypeRegistry = Record<ActionType, ActionTypeDefinition>;

/**
 * Type guard to check if an action is a PEP-C action
 */
export function isPepCAction(action: BaseAction): action is BaseAction<'pep_c'> {
  return action.action_type === 'pep_c';
}

/**
 * PEP-C action with specific payload
 */
export interface PepCAction extends BaseAction<'pep_c'> {
  action_payload: PepCPayload;
}

/**
 * Extended PEP-C payload for UI representation
 */
export interface PepCMessage {
  title: string;
  body: string;
}
